import { useMemo } from 'react';
import { prop } from 'ramda';
import { useUserSelector } from 'js/store/hooks';
import { useInitiateDocumentUpload } from 'server';
import { getRedirectUrl, handleRedirect } from 'js/components/popups/JumioVerifyPopup';

export const useJumioPublicAPI = () => {
    const initiateDocumentUpload = useInitiateDocumentUpload();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    return useMemo(() => ({
        /**
         * Retrieve jumio redirect url.
         * @memberOf Playtech.API.jumio
         * @function
         * @param {string} type
         * @returns {string}
         * @example
         * // 'https://core3-qa09.mws.playtechgaming.com/en/?jumioVerify=test'
         * Playtech.API.jumio.getRedirectUrl('test');
         */
        getRedirectUrl,
        /**
         * Perform 'handleJumioRedirect'-function from window.Playtech.API if provided by licensee or redirect to provided here url.
         * @memberOf Playtech.API.jumio
         * @function
         * @param {string} url
         * @example
         * Playtech.API.jumio.handleRedirect('https://www.example.com');
         */
        handleRedirect,
        /**
         * Perform Initiate document upload request.
         * @memberOf Playtech.API.jumio
         * @function
         * @param {Object} options Fields for request.
         * @param {string} options.successUrl
         * @param {string} options.errorUrl
         * @param {string} [options.documentType] Examples of document type: PASSPORT, DRIVING_LICENCE, ID_CARD, CCS, BS, UB.
         * @returns {Promise<{data: {redirectURL: string, reference: string}}>}
         * @example
         * Playtech.API.jumio.verifyNow({ errorUrl: 'https://error.com', successUrl: 'https://success.com', documentType: 'PASSPORT' });
         */
        verifyNow: (options) => {
            if (!isLoggedIn) {
                return Promise.reject(new Error('Jumio Public API: User must be logged in.'));
            }

            const { successUrl, errorUrl, documentType } = options;

            return initiateDocumentUpload({
                errorLevel: 0,
                service: 'netverify',
                successUrl,
                errorUrl,
                documentType
            });
        }
    }), [isLoggedIn, initiateDocumentUpload]);
};
